import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { BaseHookField, Category, Hook, HookField, PipedriveField, PreHook } from 'Types';
import { v4 as uuidv4 } from 'uuid';
import { pipedriveFieldsStore } from './PipedriveFields';
import { deepCopy } from 'utils/helpers';
import { appDataStore } from './AppData';

interface HookDefaults {
    [key: string]: {
        [key: string]: {
            value: BaseHookField["value"];
            condition: Partial<BaseHookField["condition"]>;
        }
    };
}
class HookStore {
    constructor() {
        makeAutoObservable(this);
    }

    defaultHookSettings: Record<Category, HookDefaults> = {
        deal: {
            "woocommerce_abandoned_cart": {
                "status": {
                    value: "open",
                    condition: {
                        SkipOnExist: false
                    }
                },
                "title": {
                    value: [
                        {
                            variables: [
                                {
                                    value: "billing_company",
                                    source: "user",
                                    exemple: "ABC Corp",
                                    isFreeField: false,
                                    id: "d6d51433-d8a4-404a-9432-6db4db85db44"
                                },
                                {
                                    id: "32e3ec72-dcf6-4a95-a953-6474a15b42aa",
                                    exemple: "'s Woocommerce abandoned cart",
                                    isFreeField: true,
                                    value: "'s Woocommerce abandoned cart"
                                }
                            ],
                            id: "de3e94bc-6408-4eb8-b04a-0d8cb0e79b55",
                            index: 0
                        },
                        {
                            variables: [
                                {
                                    value: "billing_first_name",
                                    source: "user",
                                    exemple: "John",
                                    isFreeField: false,
                                    id: "46764686-cbd0-4862-8425-157d7f51a268"
                                },
                                {
                                    value: "billing_last_name",
                                    source: "user",
                                    exemple: "Doe",
                                    isFreeField: false,
                                    id: "accc63d1-2e5b-472b-b9ab-d1c6e98b8a90"
                                },
                                {
                                    id: "9ad449f8-5144-485f-8626-520b0e2201dc",
                                    exemple: "'s Woocommerce abandoned cart",
                                    isFreeField: true,
                                    value: "'s Woocommerce abandoned cart"
                                }
                            ],
                            id: "4422a471-b8b9-4941-b244-a2842bbdfd7e",
                            index: 1
                        },
                        {
                            variables: [
                                {
                                    value: "user_email",
                                    source: "user",
                                    exemple: "john.doe@exemple.com",
                                    isFreeField: false,
                                    id: "19c70690-1887-4bff-9f90-c73b7cd9459c"
                                },
                                {
                                    id: "f82d890c-ec46-4f89-8d84-c301069683f0",
                                    exemple: "'s Woocommerce abandoned cart",
                                    isFreeField: true,
                                    value: "'s Woocommerce abandoned cart"
                                }
                            ],
                            id: "7180a687-c9dc-46bf-8bea-f96b52c99f68",
                            index: 2
                        }
                    ],
                    condition: {
                        logicBlock: {
                            enabled: true,
                            fieldNumber: "ALL"
                        },
                        SkipOnExist: false
                    }
                }
            },
            "woocommerce_new_order": {
                "status": {
                    value: "open",
                    condition: {
                        SkipOnExist: false
                    }
                },
            },
            "woocommerce_update_order": {} //activation pour les produits.
            ,
            "woocommerce_order_status_on-hold": {
                "status": {
                    value: "open",
                    condition: {
                        SkipOnExist: false
                    }
                },
            },
            "woocommerce_order_status_pending": {
                "status": {
                    value: "open",
                    condition: {
                        SkipOnExist: false
                    }
                },
            },
            "woocommerce_order_status_processing": {
                "status": {
                    value: "won",
                    condition: {
                        SkipOnExist: false
                    }
                },
                "won_time": {
                    value: [
                        {
                            variables: [
                                {
                                    value: "_date_paid",
                                    source: "order",
                                    exemple: "2025-03-27 14:30:00",
                                    isFreeField: false,
                                    id: "3e59362e-3cd3-4c6c-8aec-a0d350r1ab8f"
                                }
                            ],
                            id: "02e6fddc-998f-4a7f-bac3-d4539bfd3177",
                            index: 0
                        }
                    ],
                    condition: {
                        logicBlock: {
                            enabled: true,
                            fieldNumber: "ALL"
                        },
                        SkipOnExist: false
                    }
                },
            },
            "woocommerce_order_status_completed": {
                "status": {
                    value: "won",
                    condition: {
                        SkipOnExist: false
                    }
                },
                "won_time": {
                    value: [
                        {
                            variables: [
                                {
                                    value: "_completed_date",
                                    source: "order",
                                    exemple: "2025-03-27 14:30:00",
                                    isFreeField: false,
                                    id: "3e59362e-3cd3-4c6c-8aec-a0d31256ab8f"
                                }
                            ],
                            id: "02e6rrrc-998f-4a7f-bac3-d4539bfd3177",
                            index: 0
                        }
                    ],
                    condition: {
                        logicBlock: {
                            enabled: true,
                            fieldNumber: "ALL"
                        },
                        SkipOnExist: true
                    }
                },
            },
            "woocommerce_order_status_refunded": {
                "status": {
                    value: "lost",
                    condition: {
                        SkipOnExist: false
                    }
                },
            },
            "woocommerce_order_status_cancelled": {
                "status": {
                    value: "deleted",
                    condition: {
                        SkipOnExist: false
                    }
                },
            },
            "woocommerce_order_status_failed": {
                "status": {
                    value: "lost",
                    condition: {
                        SkipOnExist: false
                    }
                },
            }
        },
        organization: {
            "profile_update": {
                "name": {
                    value: [
                        {
                            variables: [
                                {
                                    value: "billing_company",
                                    source: "user",
                                    exemple: "ABC Corp",
                                    isFreeField: false,
                                    id: "3e59362e-3cd3-4c6c-8aec-a0d350b6ab8f"
                                }
                            ],
                            id: "02e6fddc-974f-4a7f-bac3-d4539bfd3177",
                            index: 0
                        }
                    ],
                    condition: {
                        logicBlock: {
                            enabled: true,
                            fieldNumber: "ALL"
                        },
                        SkipOnExist: false,
                        findInPipedrive: true
                    }
                },
                "adress": {
                    value: [
                        {
                            variables: [
                                {
                                    value: "billing_address_1",
                                    source: "user",
                                    exemple: "123 Main Street",
                                    isFreeField: false,
                                    id: "991e0668-a0ad-4b36-9070-3bed461e1bb3"
                                },
                                {
                                    value: "billing_address_2",
                                    source: "user",
                                    exemple: "Apt 4B",
                                    isFreeField: false,
                                    id: "8bd80dba-d034-480a-bcf8-fbbaf2dd275d"
                                },
                                {
                                    value: "billing_city",
                                    source: "user",
                                    exemple: "Cityville",
                                    isFreeField: false,
                                    id: "47971b56-754b-4cf9-a7a0-b26282b21f96"
                                },
                                {
                                    value: "billing_postcode",
                                    source: "user",
                                    exemple: "12345",
                                    isFreeField: false,
                                    id: "16092e20-e7a5-4edf-8aef-56a7eca84045"
                                },
                                {
                                    value: "billing_country",
                                    source: "user",
                                    exemple: "US",
                                    isFreeField: false,
                                    id: "26d81d32-0b07-4f86-af59-d075ae949d22"
                                },
                                {
                                    value: "billing_state",
                                    source: "user",
                                    exemple: "CA",
                                    isFreeField: false,
                                    id: "6a7394b0-078c-4031-87f6-d9d5762e9cd0"
                                }
                            ],
                            "id": "5e117c71-67c0-471f-a0e1-4f1e8f215bf9",
                            "index": 0
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": true,
                            "fieldNumber": "1"
                        },
                        "SkipOnExist": true
                    }
                }
            },
            "woocommerce_guest_organization_order": {
                "name": {
                    value: [
                        {
                            variables: [
                                {
                                    label: "Billing company",
                                    value: "billing_company",
                                    source: "guestorder",
                                    description: "Customer billing company.",
                                    exemple: "Acme Inc.",
                                    isFreeField: false,
                                    id: "7b928a87-bd0e-49ff-bf68-04f9cdc93a10"
                                }
                            ],
                            id: "610228ab-50cd-4ab2-867f-2034fa192fc1",
                            index: 0
                        }
                    ],
                    condition: {
                        logicBlock: {
                            enabled: false,
                            fieldNumber: "1"
                        }
                    }
                },
                "address": {
                    value: [
                        {
                            variables: [
                                {
                                    label: "Billing address 1",
                                    value: "billing_address_1",
                                    source: "guestorder",
                                    description: "Customer billing street address (line 1).",
                                    exemple: "123 Main Street",
                                    isFreeField: false,
                                    id: "6a94af9b-f273-4afe-a1d2-30bf25029b9a"
                                },
                                {
                                    label: "Billing address 2",
                                    value: "billing_address_2",
                                    source: "guestorder",
                                    description: "Customer billing street address (line 2).",
                                    exemple: "Apartment 4B",
                                    isFreeField: false,
                                    id: "e3ee0463-8800-42c0-b69a-65c7695c5ac9"
                                },
                                {
                                    label: "Billing city",
                                    value: "billing_city",
                                    source: "guestorder",
                                    description: "Customer billing city.",
                                    exemple: "New York",
                                    isFreeField: false,
                                    id: "b48bbe77-8fb3-43bf-ae3d-e0f3bd555d89"
                                },
                                {
                                    label: "Billing postcode",
                                    value: "billing_postcode",
                                    source: "guestorder",
                                    description: "Customer billing postal code.",
                                    exemple: "10001",
                                    isFreeField: false,
                                    id: "92ea3220-6550-478f-8942-f75fc8607581"
                                },
                                {
                                    label: "Billing state",
                                    value: "billing_state",
                                    source: "guestorder",
                                    description: "Customer billing state or province.",
                                    exemple: "NY",
                                    isFreeField: false,
                                    id: "0c513707-d73f-44bc-bc5b-c6be02fc3a08"
                                },
                                {
                                    label: "Billing country",
                                    value: "billing_country",
                                    source: "guestorder",
                                    description: "Customer billing country.",
                                    exemple: "US",
                                    isFreeField: false,
                                    id: "e467481b-2718-414f-93de-54f22f099c7e"
                                }
                            ],
                            id: "8f480a81-5e43-4933-bcb2-32180966c0a1",
                            index: 0
                        }
                    ],
                    condition: {
                        logicBlock: {
                            enabled: false,
                            fieldNumber: "1"
                        }
                    }
                }
            }

        },
        person: {
            "user_register": {
                "name": {
                    value: [
                        {
                            "variables": [
                                {
                                    "value": "first_name",
                                    "source": "user",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "9f6bb149-9881-4029-ae51-ece68ce7fb15"
                                },
                                {
                                    "value": "last_name",
                                    "source": "user",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "d90d63b5-5536-4055-b69e-04292204ecc6"
                                }
                            ],
                            "id": "95ac6390-03ed-4586-8c82-5c75c2e85ed7",
                            "index": 0
                        },
                        {
                            "variables": [
                                {
                                    "value": "billing_first_name",
                                    "source": "user",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "51aff9bd-d8d0-4eb8-906d-c9252f69f75b"
                                },
                                {
                                    "value": "billing_last_name",
                                    "source": "user",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "f8c6de80-9964-46bd-b88a-c70e306b457b"
                                }
                            ],
                            "id": "a19ae5c7-74cd-4864-a15c-a86cccb3f793",
                            "index": 1
                        },
                        {
                            "variables": [
                                {
                                    "value": "user_email",
                                    "source": "user",
                                    "exemple": "john.doe@exemple.com",
                                    "isFreeField": false,
                                    "id": "062a3Rfc-9bfa-4188-a066-2d90093e9e74"
                                }
                            ],
                            "id": "37659736-d9d3-448f-b693-810c777a4d61",
                            "index": 2
                        },
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": true,
                            "fieldNumber": "ALL"
                        },
                        "findInPipedrive": true,
                        "SkipOnExist": true
                    },
                },
                "emails": {
                    value: [
                        {
                            "variables": [
                                {
                                    "value": "user_email",
                                    "exemple": "john.doe@exemple.com",
                                    "isFreeField": false,
                                    "source": "user",
                                    "id": "062a38fc-9bfa-4188-a066-2d90093e9e74"
                                }
                            ],
                            "id": "37659736-d9d3-448f-b693-810c777a4d61",
                            "index": 0
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        },
                        "findInPipedrive": true,
                        "SkipOnExist": false
                    },
                },
                "first_name": {
                    value: [{
                        "variables": [
                            {
                                "label": "First name",
                                "value": "first_name",
                                "source": "user",
                                "description": "User's first name.",
                                "exemple": "John",
                                "isFreeField": false,
                                "id": "31a9403f-883d-445c-bc7a-48df73aa73c7"
                            }
                        ],
                        "id": "7c305eda-b182-44e1-868e-58b14e1d93b1",
                        "index": 0

                    }],
                    condition: {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        }
                    }
                },
                "last_name": {
                    value: [
                        {
                            "variables": [
                                {
                                    "label": "Last name",
                                    "value": "last_name",
                                    "source": "user",
                                    "description": "User's last name.",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "03391c8f-312c-4317-86e8-733cd91921d6"
                                }
                            ],
                            "id": "85d9fdf0-a775-40a5-a965-a887d6561e3e",
                            "index": 0
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        }
                    }
                }
            },
            "profile_update": {
                "name": {
                    value: [
                        {
                            "variables": [
                                {
                                    "value": "first_name",
                                    "source": "user",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "9f6bb149-9881-4029-ae51-ece68ce7fb15"
                                },
                                {
                                    "value": "last_name",
                                    "source": "user",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "d90d63b5-5536-4055-b69e-04292204ecc6"
                                }
                            ],
                            "id": "95ac6390-03ed-4586-8c82-5c75c2e85ed7",
                            "index": 0
                        },
                        {
                            "variables": [
                                {
                                    "value": "billing_first_name",
                                    "source": "user",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "51aff9bd-d8d0-4eb8-906d-c9252f69f75b"
                                },
                                {
                                    "value": "billing_last_name",
                                    "source": "user",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "f8c6de80-9964-46bd-b88a-c70e306b457b"
                                }
                            ],
                            "id": "a19ae5c7-74cd-4864-a15c-a86cccb3f793",
                            "index": 1
                        },
                        {
                            "variables": [
                                {
                                    "value": "user_email",
                                    "source": "user",
                                    "exemple": "john.doe@exemple.com",
                                    "isFreeField": false,
                                    "id": "062a38fc-9bfa-4188-a066-2d90093eae74"
                                }
                            ],
                            "id": "3ed59736-d9d3-448f-b693-810c777a4d61",
                            "index": 2
                        },
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": true,
                            "fieldNumber": "ALL"
                        },
                        "findInPipedrive": false,
                        "SkipOnExist": true
                    },
                },
                "emails": {
                    value: [
                        {
                            "variables": [
                                {
                                    "value": "user_email",
                                    "source": "user",
                                    "exemple": "john.doe@exemple.com",
                                    "isFreeField": false,
                                    "id": "062a38fc-9bfa-4188-a066-2d90e93e9e74"
                                }
                            ],
                            "id": "37659736-d9d3-448f-b693-810c777a4d61",
                            "index": 0
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        },
                        "findInPipedrive": true,
                        "SkipOnExist": false
                    },
                },
                "first_name": {
                    value: [
                        {
                            "variables": [
                                {
                                    "label": "First name",
                                    "value": "first_name",
                                    "source": "user",
                                    "description": "User's first name.",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "e4d97742-13cd-464b-9ef2-69c5f415c0d7"
                                }
                            ],
                            "id": "b1888144-2388-4db2-a33c-2f9f35bda99d",
                            "index": 0
                        },
                        {
                            "variables": [
                                {
                                    "label": "Billing first_name",
                                    "value": "billing_first_name",
                                    "source": "user",
                                    "description": "Customer's billing first name.",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "2f71c06a-077c-4e21-adbd-bd72c98f5039"
                                }
                            ],
                            "id": "187d7260-af4a-4740-9b34-ad0cb81e187a",
                            "index": 1
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": true,
                            "fieldNumber": "1"
                        }
                    }
                },
                "last_name": {
                    value: [
                        {
                            "variables": [
                                {
                                    "label": "Last name",
                                    "value": "last_name",
                                    "source": "user",
                                    "description": "User's last name.",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "b1296850-3522-4516-a083-f35d452c6a78"
                                }
                            ],
                            "id": "0899328f-95a1-481e-a433-6e5c4b9f5e53",
                            "index": 0
                        },
                        {
                            "variables": [
                                {
                                    "label": "Billing last name",
                                    "value": "billing_last_name",
                                    "source": "user",
                                    "description": "Customer's billing last name.",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "0ac6a99f-e3fa-4217-a42b-1ec7e087652f"
                                }
                            ],
                            "id": "dbf85761-e943-4480-86a1-45690ef9edf2",
                            "index": 1
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": true,
                            "fieldNumber": "1"
                        }
                    }
                },
                "phone": {
                    value: [
                        {
                            "variables": [
                                {
                                    "label": "Billing phone",
                                    "value": "billing_phone",
                                    "source": "user",
                                    "description": "Customer's billing phone number.",
                                    "exemple": "123-456-7890",
                                    "isFreeField": false,
                                    "id": "3dd480b5-f8d0-48ca-90d2-e6966e4b7e8d"
                                }
                            ],
                            "id": "587d354f-d9e5-4196-b941-651cb87f3230",
                            "index": 0
                        }
                    ],
                    condition: {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        }
                    }
                }
            },
            "woocommerce_guest_person_order": {
                "name": {
                    "value": [
                        {
                            "variables": [
                                {
                                    "label": "Billing first name",
                                    "value": "billing_first_name",
                                    "source": "guestorder",
                                    "description": "Customer billing first name.",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "eacddd70-6878-4b8f-b197-426660488372"
                                },
                                {
                                    "label": "Billing last name",
                                    "value": "billing_last_name",
                                    "source": "guestorder",
                                    "description": "Customer billing last name.",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "f4e5ac22-1016-407e-b875-27ece2a36e93"
                                }
                            ],
                            "id": "c262e0bb-0700-461b-bcae-005394a6d643",
                            "index": 0
                        }
                    ],
                    "condition": {
                        "logicBlock": {
                            "enabled": true,
                            "fieldNumber": "ALL"
                        }
                    },
                },
                "phone": {
                    "value": [
                        {
                            "variables": [
                                {
                                    "label": "Billing phone",
                                    "value": "billing_phone",
                                    "source": "guestorder",
                                    "description": "Customer billing phone number.",
                                    "exemple": "+33123456789",
                                    "isFreeField": false,
                                    "id": "3e374e7a-149f-440c-8004-8a8170ec2dfa"
                                }
                            ],
                            "id": "dbcfc635-1a7b-42ce-865f-7bcb0c6e4314",
                            "index": 0
                        }
                    ],
                    "condition": {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        }
                    },
                },
                "emails": {
                    "value": [
                        {
                            "variables": [
                                {
                                    "label": "Billing email",
                                    "value": "billing_email",
                                    "source": "guestorder",
                                    "description": "Customer billing email.",
                                    "exemple": "john.doe@example.com",
                                    "isFreeField": false,
                                    "id": "cd2260ac-139d-4c84-8914-4b1f0d2841d0"
                                }
                            ],
                            "id": "84c82ae8-3168-4d77-9ecd-6840fb0e1079",
                            "index": 0,
                        }
                    ],
                    "condition": {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        },
                        "findInPipedrive": true,
                    },
                },
                "first_name": {
                    "value": [
                        {
                            "variables": [
                                {
                                    "label": "Billing first name",
                                    "value": "billing_first_name",
                                    "source": "guestorder",
                                    "description": "Customer billing first name.",
                                    "exemple": "John",
                                    "isFreeField": false,
                                    "id": "28e1771c-d8b4-4028-9dd8-44b0d2dd0287"
                                }
                            ],
                            "id": "dd298559-0ddc-4bd0-b261-ad4d9cacc89c",
                            "index": 0
                        }
                    ],
                    "condition": {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        }
                    },
                },
                "last_name": {
                    "value": [
                        {
                            "variables": [
                                {
                                    "label": "Billing last name",
                                    "value": "billing_last_name",
                                    "source": "guestorder",
                                    "description": "Customer billing last name.",
                                    "exemple": "Doe",
                                    "isFreeField": false,
                                    "id": "1345047d-75f2-4a0b-9015-a9f0601ecbcc"
                                }
                            ],
                            "id": "f26d930a-fe37-45bf-ad30-c09e5097d141",
                            "index": 0
                        }
                    ],
                    "condition": {
                        "logicBlock": {
                            "enabled": false,
                            "fieldNumber": "1"
                        }
                    },
                }
            }
        }
    };

    emptyHookField: BaseHookField = {
        id: '',
        enabled: false,
        value: 0,
        condition: {
            logicBlock: {
                enabled: false,
                fieldNumber: '1'
            }
        },
        pipedriveFieldId: '',
        hookId: ''
    };

    emptyHook: Hook = {
        id: "",
        label: "",
        key: "",
        source: "user",
        disabledFor: [],
        description: "",
        show: false,
        enabled: false,
        option: {
            createActivity: false,
            activity: {
                subject: "",
                due_time: "09:00",
                due_date_unit: "day",
                due_date_value: 2,
                enable_due_date: true,
                done: false,
                note: "",
                owner_id: 0,
                type: "",
            }
        },
        category: "person",
        fields: [],
    };

    hooks: Hook[] = []

    defaultGuestOrderHook: Hook = {
        label: 'Guest Order (person)',
        key: 'woocommerce_guest_person_order',
        description: 'Fired when an order is using a guest as customer.',
        disabledFor: ['person', 'organization', 'deal'], // HIDDEN
        source: 'guestorder',
        category: 'person',
        show: false,
        fields: [],
        id: 'e85ac0d4-492c-480b-aa60-2f46b3e61005',
        enabled: false,
        option: this.emptyHook.option
    }

    defaultGuestOrganizationOrderHook: Hook = {
        label: 'Guest Order (organization)',
        key: 'woocommerce_guest_organization_order',
        description: 'Fired when an order is using a guest as organization.',
        disabledFor: ['person', 'organization', 'deal'], // HIDDEN
        source: 'guestorder',
        category: 'organization',
        show: false,
        fields: [],
        id: 'e85ac0d4-492c-480b-aa60-2f46b3e61006',
        enabled: false,
        option: this.emptyHook.option
    }

    selectedHookId: Hook["id"] | null = null

    selectHookId(id: Hook["id"] | null) {
        if (id) {
            runInAction(() => {
                const hook = this.getHook(id);
                this.selectedHookId = hook ? hook.id : null;
            });
        } else {
            runInAction(() => {
                this.selectedHookId = null;
            });
        }
    }

    addNewHook(hook: Hook) {
        runInAction(() => {
            const validatedHook = this.validHook(hook)
            validatedHook && this.hooks.push(validatedHook)
        })
    }

    updateHook(id: string, updatedData: Partial<Hook>) {
        const hookIndex = this.hooks.findIndex(hook => hook.id === id);
        if (hookIndex > -1) {
            runInAction(() => {
                this.hooks[hookIndex] = { ...this.hooks[hookIndex], ...updatedData };
            })
        }
    }

    validHook(hook: Hook): Hook | null {
        const referenceHook = appDataStore.appData.CONSTANTES.W2PCIFW_HOOK_LIST
            .find(hookRef => hook.key === hookRef.key)
        if (referenceHook) {
            return {
                ...hook,
                ...referenceHook,
            }
        } else {
            return null
        }
    }

    updateHookList(newHookList: Hook[]) {
        const guestOrderHook = this.getHook(this.defaultGuestOrderHook.id)
        const guestOrganizationOrderHook = this.getHook(this.defaultGuestOrganizationOrderHook.id)
        runInAction(() => {
            const newHooks = newHookList
                .map(hook => this.validHook(hook))
                .filter((hook): hook is Hook => hook !== null);

            if (!guestOrderHook) newHooks.push(toJS(this.defaultGuestOrderHook))
            if (!guestOrganizationOrderHook) newHooks.push(toJS(this.defaultGuestOrganizationOrderHook))

            this.hooks = newHooks
        })
    }

    getHookFromPreHook(preHook: PreHook, category: Category): Hook {
        const wantedHook = this.hooks.find(h => preHook.key === h.key && category === h.category);

        const hook = wantedHook
            ? this.getHook(wantedHook.id)
            : null

        if (hook) {
            return hook;
        } else {
            //Obligé de deepCopy sinon renvoie les mêmes fields pour tous les Hook o:
            const newHook = deepCopy({ ...this.emptyHook, ...preHook, category, id: uuidv4() });
            this.addNewHook(newHook);
            // Auto-creation: keep dirty-tracking baseline aligned so the user is not prompted for a change they never made
            appDataStore.addInitHook(newHook);
            return newHook;
        }
    }

    getHook(id: string): Hook | null {
        const wantedHook = this.hooks.find(hook => hook.id === id)
        if (wantedHook) {
            const fields = this.getFields(wantedHook.id)
            return { ...wantedHook, fields: fields };
        }
        return null;
    }

    getHookIndex(id: string): number {
        const wantedHookIndex = this.hooks.findIndex(hook => hook.id === id)
        return wantedHookIndex;
    }

    getFields(hookId: string): HookField[] {
        const hookIndex = this.getHookIndex(hookId)

        const fields = this.hooks[hookIndex].fields
            .map(field => this.formatHookField(field))
            .filter((field): field is HookField => field !== null)

        //Obligé de deepCopy sinon renvoie les mêmes fields pour tous les Hook o:
        return deepCopy(fields)
    }


    /********** HOOK FIELDS ***********/

    updateHookFieldsFromPipedriveFields(pipedriveFields: PipedriveField[]): void {
        this.hooks.forEach((hook, idx) => {
            this.addHookFieldsFromPipedrive(hook.category, hook.id, pipedriveFields, idx)
        })
    }

    addHookFieldsFromPipedrive(category: Category, hookId: string, pipedriveFields: PipedriveField[], hookIndex: number | null = null): void {
        const hookIdx = hookIndex
            ? hookIndex
            : hookStore.getHookIndex(hookId);

        if (hookIdx > -1) {
            pipedriveFields
                .forEach(pipedriveField => {
                    if (pipedriveField.category === category) {
                        this.addHookField(hookId, pipedriveField, hookIdx)
                    }
                })
        }
    }

    addHookField(hookId: string, pipedriveField: PipedriveField, hookIndex?: number): HookField | null {
        if (pipedriveFieldsStore.isFieldValid(pipedriveField)) {
            const hIdx = hookIndex ?? hookStore.getHookIndex(hookId);

            const existingField = this.hooks[hIdx].fields.find(field => field.pipedriveFieldId === pipedriveField.field_code)

            if (!existingField) {
                const newHookField: HookField = {
                    ...this.emptyHookField,
                    id: uuidv4(),
                    pipedriveFieldId: pipedriveField.field_code,
                    pipedrive: pipedriveField,
                    hookId,
                };

                runInAction(() => {

                    this.hooks[hIdx].fields.push(deepCopy(newHookField));
                    return newHookField;
                })
            } else {
                runInAction(() => {
                    this.hooks[hIdx].fields = this.hooks[hIdx].fields.map(field =>
                        field.pipedriveFieldId === pipedriveField.field_code
                            ? { ...field, pipedrive: deepCopy(pipedriveField) }
                            : field
                    );
                });

                return existingField;

            }
        }
        return null;
    }

    getHookFieldFromPipedrive(hookId: Hook["id"], pipedriveFieldId: PipedriveField["field_code"]): HookField | null {
        const hook = hookStore.getHook(hookId);

        if (hook) {
            const hookField = hook.fields.find(hook => hook.pipedriveFieldId === pipedriveFieldId)
            if (hookField) {
                return this.formatHookField(hookField)
            } else {
                const pipedriveField = pipedriveFieldsStore.getPipedriveField(pipedriveFieldId, hook.category);
                return pipedriveField
                    ? this.addHookField(hookId, pipedriveField)
                    : null
            }
        } else {
            return null
        }
    }

    formatHookField(field: BaseHookField | HookField): HookField | null {
        if ("pipedrive" in field) {
            return field;
        } else {
            const hook = this.hooks.find(hook => hook.id === field.hookId)
            if (hook) {
                const pipedriveField = pipedriveFieldsStore.getPipedriveField(field.pipedriveFieldId, hook.category);
                const formatedField = pipedriveField
                    ? {
                        ...field,
                        pipedrive: pipedriveField,
                    }
                    : null;

                return formatedField
            } else {
                return null
            }
        }
    }

    updateHookField(hook: Hook, fieldId: string, updatedData: Partial<HookField>): void {
        const hookIdx = this.getHookIndex(hook.id)
        if (hookIdx > -1) {
            const hookFieldIndex = this.hooks[hookIdx].fields.findIndex(hookField => hookField.id === fieldId);
            if (hookFieldIndex > -1) {
                runInAction(() => {
                    this.hooks[hookIdx].fields[hookFieldIndex] = { ...this.hooks[hookIdx].fields[hookFieldIndex], ...updatedData };
                })
            }
        }
    }
}

export const hookStore = new HookStore();
